import { getWindowSafe } from '@change-corgi/core/window';

import { START_STRONG_FEATURE } from '#src/app/shared/constants';

/**
 * New Relic and Sentry telemetry for the Start Strong after-SAP flow (CHANGE-100081).
 *
 * Pure functions, no hooks, and kept in app-shared: the redirect event is recorded from the SAP
 * publish flow, which sits outside both the Start Strong flow provider and the `starter` page group.
 * Attribute names reuse the dashboard's `baseAttrs` names (`useHandlePolling`) so NRQL can query both
 * with the same shape.
 */

/**
 * The parts of the flow's `StartStrongFlowSurface` telemetry reads. Restated structurally because
 * app-shared code can't import from the `starter` page group.
 */
type StartStrongSurface = Readonly<{
	id: 'dashboard_modal' | 'after_sap_page' | 'dashboard_task_modal';
	newRelic: Readonly<{ pageSection: string; pageVersion: string }>;
}>;

type PageActionValue = string | number | boolean;
type PageActionAttrs = Readonly<Record<string, PageActionValue | undefined>>;

type BaseAttrsParams = Readonly<{
	surface?: Pick<StartStrongSurface, 'newRelic'>;
	petitionId: string;
	petitionSlug: string | null;
	countryCode: string;
	experimentVariant?: string;
	gqlOperation: string;
	flowKind: 'redirect' | 'poll' | 'action';
}>;

export function buildStartStrongBaseAttrs({
	surface,
	petitionId,
	petitionSlug,
	countryCode,
	experimentVariant,
	gqlOperation,
	flowKind,
}: BaseAttrsParams): PageActionAttrs {
	// Sent as a number so NRQL can join on the backend's integer `petition_id`. A non-numeric id is
	// dropped rather than sent as `NaN`.
	const numericPetitionId = Number(petitionId);
	return {
		feature: START_STRONG_FEATURE,
		pageSection: surface?.newRelic.pageSection,
		pageVersion: surface?.newRelic.pageVersion,
		gqlOperation,
		country: countryCode.toLowerCase(),
		petitionSlug: petitionSlug ?? undefined,
		petitionId: Number.isFinite(numericPetitionId) ? numericPetitionId : undefined,
		experimentVariant,
		flowKind,
	};
}

type SentryContextParams = Readonly<{
	/** `null` only when there is no flow provider, which happens in tests alone. */
	surface: StartStrongSurface | null;
	operation: string;
	petitionSlug?: string;
}>;

/**
 * The `sd.*` Sentry tags for a Start Strong error. The dashboard surfaces keep `sd.section: 'tasks'`
 * so their existing Sentry filters keep matching.
 */
export function getStartStrongSentryContext({
	surface,
	operation,
	petitionSlug,
}: SentryContextParams): Readonly<Record<string, string>> {
	return {
		'sd.section': surface?.id === 'after_sap_page' ? 'start_strong' : 'tasks',
		'sd.operation': operation,
		'sd.version': surface?.newRelic.pageVersion ?? 'v2',
		...(petitionSlug === undefined ? {} : { 'petition.slug': petitionSlug }),
		feature: START_STRONG_FEATURE,
	};
}

function addPageAction(name: string, attrs: PageActionAttrs): void {
	const newrelic = getWindowSafe()?.newrelic;
	if (!newrelic) return;
	// `addPageAction` only accepts string, number or boolean values.
	const defined = Object.fromEntries(
		Object.entries(attrs).filter((entry): entry is [string, PageActionValue] => entry[1] !== undefined),
	);
	newrelic.addPageAction(name, defined);
}

export type StartStrongTreatOutcome =
	| 'treated'
	| 'control'
	| 'override'
	| 'not_first_petition'
	| 'timeout'
	| 'not_treated'
	| 'ineligible';

type RedirectResolvedAttrs = PageActionAttrs &
	Readonly<{
		destination: 'start_strong' | 'starter_onboarding' | 'ambassador';
		treatOutcome: StartStrongTreatOutcome;
		resolveLatencyMs: number;
	}>;

export function recordRedirectResolved(attrs: RedirectResolvedAttrs): void {
	addPageAction('StartStrongRedirectResolved', attrs);
}
